/**
 * AgentSchedulesModal — konfiguracja tygodniowych harmonogramów agentów.
 *
 * Zakres (2026-02, round 18 = Faza 4 UI):
 *   - Agent Faktur (Invoice Agent) — pełny cykl analizy vendorów raz na tydzień.
 *   - Agent Płatności (Payment Agent) — full-run tygodniowo + legacy 5-min
 *     autoanaliza nowych kontrahentów (osobny toggle).
 *
 * Fundament backendowy: round 13 (Invoice weekly) + round 15/16 (Payment weekly
 * + shared lock + Invoice cycle correctness). Ten komponent jest **czysto UI**
 * — cała logika idzie przez `GET /api/agent-settings` i `PUT /api/agent-settings`.
 *
 * Kluczowa reguła Payment Agenta (M676):
 *   `statement_max_age_days` — per-user, brak sztywnego defaultu. `null`
 *   wyłącza kontrolę świeżości importu wyciągu.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Loader2, Save, AlertCircle, Info, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = [
  { value: 0, label: "Poniedziałek" },
  { value: 1, label: "Wtorek" },
  { value: 2, label: "Środa" },
  { value: 3, label: "Czwartek" },
  { value: 4, label: "Piątek" },
  { value: 5, label: "Sobota" },
  { value: 6, label: "Niedziela" },
];

const formatNextRun = (iso) => {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("pl-PL", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Warsaw",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const formatLastRun = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("pl-PL", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Warsaw",
    }).format(d);
  } catch {
    return iso;
  }
};

/**
 * Wspólna sekcja „harmonogram tygodniowy" — reużyta przez oba agenty
 * (mają identyczny podzbiór pól: enabled, weekday, time_hhmm, timezone).
 */
const ScheduleFieldset = ({ agentKey, values, onChange, disabled }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-gray-50">
      <div>
        <div className="text-sm font-medium text-gray-900">Automatyczna analiza</div>
        <div className="text-xs text-gray-500 mt-0.5">
          Uruchamiaj analizę raz w tygodniu o wyznaczonej porze.
        </div>
      </div>
      <Switch
        checked={!!values.enabled}
        onCheckedChange={(v) => onChange({ enabled: v })}
        disabled={disabled}
        data-testid={`sched-${agentKey}-enabled`}
      />
    </div>

    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${values.enabled ? "" : "opacity-50 pointer-events-none"}`}>
      <div>
        <Label htmlFor={`sched-${agentKey}-weekday`} className="text-xs">Dzień tygodnia</Label>
        <select
          id={`sched-${agentKey}-weekday`}
          value={values.weekday ?? 3}
          onChange={(e) => onChange({ weekday: parseInt(e.target.value, 10) })}
          className="w-full h-10 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 mt-1"
          disabled={disabled}
          data-testid={`sched-${agentKey}-weekday`}
        >
          {WEEKDAYS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
      </div>
      <div>
        <Label htmlFor={`sched-${agentKey}-time`} className="text-xs">Godzina (Europe/Warsaw)</Label>
        <input
          id={`sched-${agentKey}-time`}
          type="time"
          value={values.time_hhmm || "03:00"}
          onChange={(e) => onChange({ time_hhmm: e.target.value })}
          className="w-full h-10 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 mt-1"
          disabled={disabled}
          data-testid={`sched-${agentKey}-time`}
        />
      </div>
    </div>

    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border bg-indigo-50/40 text-xs">
      <div className="flex items-center gap-2 text-gray-700">
        <CalendarClock className="w-3.5 h-3.5 text-indigo-600" />
        <span>Następne uruchomienie:</span>
      </div>
      <span className="font-medium text-indigo-900" data-testid={`sched-${agentKey}-next-run`}>
        {values.enabled ? formatNextRun(values.next_scheduled_run_at) : "Wyłączone"}
      </span>
    </div>

    {values.last_scheduled_run_at ? (
      <div className="flex items-center justify-between gap-3 px-3 py-1 text-[11px] text-gray-500">
        <span>Ostatnie uruchomienie:</span>
        <span data-testid={`sched-${agentKey}-last-run`}>{formatLastRun(values.last_scheduled_run_at)}</span>
      </div>
    ) : null}
  </div>
);

const AgentSchedulesModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [invoiceAgent, setInvoiceAgent] = useState(null);
  const [paymentAgent, setPaymentAgent] = useState(null);
  // Osobny state dla input `statement_max_age_days` — sterowany checkboxem
  // „Kontroluj świeżość". null = kontrola wyłączona (spec M676).
  const [stmtAgeCheck, setStmtAgeCheck] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/agent-settings`, { withCredentials: true });
      const ia = res.data?.invoice_agent || {};
      const pa = res.data?.payment_agent || {};
      setInvoiceAgent(ia);
      setPaymentAgent(pa);
      setStmtAgeCheck(pa.statement_max_age_days !== null && pa.statement_max_age_days !== undefined);
    } catch (e) {
      setError(e.response?.data?.detail?.message || "Nie udało się wczytać ustawień.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const patchInvoice = (patch) => setInvoiceAgent((prev) => ({ ...(prev || {}), ...patch }));
  const patchPayment = (patch) => setPaymentAgent((prev) => ({ ...(prev || {}), ...patch }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        invoice_agent: {
          enabled: !!invoiceAgent.enabled,
          frequency: "weekly",
          weekday: invoiceAgent.weekday ?? 3,
          time_hhmm: invoiceAgent.time_hhmm || "03:00",
          timezone: invoiceAgent.timezone || "Europe/Warsaw",
        },
        payment_agent: {
          enabled: !!paymentAgent.enabled,
          frequency: "weekly",
          weekday: paymentAgent.weekday ?? 4,
          time_hhmm: paymentAgent.time_hhmm || "03:00",
          timezone: paymentAgent.timezone || "Europe/Warsaw",
          // spec M676: null = kontrola wyłączona; dodatnia int = limit dni.
          statement_max_age_days: stmtAgeCheck
            ? Math.max(1, parseInt(paymentAgent.statement_max_age_days ?? 7, 10) || 7)
            : null,
          automatically_analyze_new_contractors: !!paymentAgent.automatically_analyze_new_contractors,
        },
      };
      const res = await axios.put(`${API}/agent-settings`, payload, { withCredentials: true });
      // Re-hydruj computed next_scheduled_run_at z odpowiedzi.
      setInvoiceAgent(res.data?.invoice_agent || invoiceAgent);
      setPaymentAgent(res.data?.payment_agent || paymentAgent);
      toast.success("Harmonogramy zapisane.");
    } catch (e) {
      const msg = e.response?.data?.detail?.message || "Nie udało się zapisać ustawień.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const canSave = useMemo(
    () => !loading && !saving && !!invoiceAgent && !!paymentAgent,
    [loading, saving, invoiceAgent, paymentAgent],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="agent-schedules-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-indigo-600" />
            Harmonogramy agentów
          </DialogTitle>
          <DialogDescription>
            Ustaw kiedy Agent Faktur i Agent Płatności mają automatycznie analizować dane.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-gray-500" data-testid="agent-schedules-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            Wczytywanie ustawień…
          </div>
        ) : (
          <Tabs defaultValue="invoice" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="invoice" data-testid="agent-schedules-tab-invoice">Agent Faktur</TabsTrigger>
              <TabsTrigger value="payment" data-testid="agent-schedules-tab-payment">Agent Płatności</TabsTrigger>
            </TabsList>

            <TabsContent value="invoice" className="mt-4">
              {invoiceAgent && (
                <ScheduleFieldset
                  agentKey="invoice"
                  values={invoiceAgent}
                  onChange={patchInvoice}
                  disabled={saving}
                />
              )}
              <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900 flex gap-2">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-600" />
                <div>
                  Agent Faktur analizuje nowych kontrahentów i weryfikuje profile płatnicze.
                  Cykl uruchamia się tylko raz w wybranym oknie — kolejny start dopiero za tydzień.
                </div>
              </div>
            </TabsContent>

            <TabsContent value="payment" className="mt-4 space-y-4">
              {paymentAgent && (
                <>
                  <ScheduleFieldset
                    agentKey="payment"
                    values={paymentAgent}
                    onChange={patchPayment}
                    disabled={saving}
                  />

                  {/* Kontrola świeżości importu wyciągu (spec M676) */}
                  <div className="p-3 rounded-lg border bg-white space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <Label className="text-sm font-medium text-gray-900">
                          Kontrola świeżości wyciągu bankowego
                        </Label>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          Blokuje analizę gdy ostatni import wyciągu jest starszy niż limit.
                        </div>
                      </div>
                      <Switch
                        checked={stmtAgeCheck}
                        onCheckedChange={setStmtAgeCheck}
                        disabled={saving}
                        data-testid="sched-payment-stmt-check"
                      />
                    </div>
                    {stmtAgeCheck ? (
                      <div className="flex items-center gap-2 pt-1">
                        <Label htmlFor="sched-payment-max-age" className="text-xs shrink-0">Maks. wiek importu:</Label>
                        <input
                          id="sched-payment-max-age"
                          type="number"
                          min={1}
                          step={1}
                          value={paymentAgent.statement_max_age_days ?? 7}
                          onChange={(e) => patchPayment({ statement_max_age_days: parseInt(e.target.value, 10) })}
                          className="w-20 h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                          disabled={saving}
                          data-testid="sched-payment-max-age"
                        />
                        <span className="text-xs text-gray-600">dni</span>
                      </div>
                    ) : (
                      <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 flex items-start gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        Kontrola wyłączona — analiza uruchomi się nawet gdy wyciąg jest bardzo stary.
                      </div>
                    )}
                  </div>

                  {/* Legacy 5-min autoanaliza — osobny toggle */}
                  <div className="p-3 rounded-lg border bg-white flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-amber-600" />
                        <Label className="text-sm font-medium text-gray-900">
                          Analizuj nowych kontrahentów natychmiast
                        </Label>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5">
                        Osobny mechanizm: co 5 minut łapie nowych kontrahentów tuż po pojawieniu się
                        pierwszej faktury. Niezależny od harmonogramu tygodniowego.
                      </div>
                    </div>
                    <Switch
                      checked={!!paymentAgent.automatically_analyze_new_contractors}
                      onCheckedChange={(v) => patchPayment({ automatically_analyze_new_contractors: v })}
                      disabled={saving}
                      data-testid="sched-payment-legacy-auto"
                    />
                  </div>
                </>
              )}

              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900 flex gap-2">
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-blue-600" />
                <div>
                  Agent Płatności startuje tylko gdy Agent Faktur zakończył wymagany cykl
                  i workflow jest wolny (brak trwających importów, analiz, KSeF).
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {error ? (
          <div
            className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 flex items-start gap-2"
            data-testid="agent-schedules-error"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        ) : null}

        <DialogFooter className="flex-row justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="agent-schedules-close-btn"
          >
            Anuluj
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="agent-schedules-save-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Zapisywanie…
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Zapisz
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentSchedulesModal;
